import { currencyRegistry } from '@root/entities/currency/atoms'
import { CurrencyAddress, CurrencyModel } from '@root/entities/currency/model'
import { Network } from '@root/entities/network'
import { PairAddress } from '@root/entities/pair/model/PairAddress'
import { DelugeId } from '@root/entities/user-short/model/DelugeId'

import * as CommentAuthor from './comment-author'
import { CommentId } from './comment-id'

import type { User } from '@root/entities/auth/model'
import type { CommentApi } from '@root/shared/api'
import type { CommentDto } from '@root/shared/api/CommentApi'
import type { AvatarsHost, TokenLogosHost } from '@root/shared/models'

export type CommentType = 'comments' | 'replies'

export const COMMENT_DEPTH = 10
export const COMMENT_HISTORY_PAGINATION = 10

export interface CommentsHistory {
  pair: {
    address: PairAddress
    quote: CurrencyModel
    base: CurrencyModel
  }
  comments: Array<Comment>
  totalCount: number
}

export interface CommentsThread {
  comments: Array<Comment>
  totalCount: number
}

export interface Comment {
  id: CommentId
  createdAt: string
  updatedAt: string
  content: string
  author: CommentAuthor.CommentAuthor
  authorDelugeId: DelugeId
  pairAddress: PairAddress
  parentCommentId?: CommentId
  mentions: Array<string>
  imageAttachment?: string

  reactions?: Record<string, { count: number; users: Array<string> }>
  replies: Array<Comment>
  totalReplies: number
  depth: number
}

export const fromDto = (
  dto: CommentDto,
  options: {
    avatarHost: AvatarsHost
  },
): Comment => ({
  ...dto,
  id: CommentId(dto.id),
  parentCommentId: dto.parentCommentId
    ? CommentId(dto.parentCommentId)
    : undefined,
  pairAddress: PairAddress(dto.pairAddress),
  replies:
    dto.replies
      ?.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map((reply) => fromDto(reply, options)) ?? [],
  authorDelugeId: DelugeId(dto.authorDelugeId),
  author: CommentAuthor.fromDto(dto.author, {
    avatarsHost: options.avatarHost,
  }),
})

export const threadFromDto = (
  dto: CommentApi.GetCommentsRespDto,
  options: {
    avatarHost: AvatarsHost
  },
): CommentsThread => ({
  comments: dto.comments.map((item) => fromDto(item, options)),
  totalCount: dto.totalCount,
})

export const commentsHistoryFromDto = (
  dto: CommentApi.GetCommentsHistoryRespDto,
  options: {
    tokenLogosHost: TokenLogosHost
    avatarHost: AvatarsHost
  },
): Array<CommentsHistory> =>
  dto.map((it) => ({
    pair: {
      address: PairAddress(it.pair.address),
      quote: currencyRegistry.getOrCreate(
        CurrencyAddress(it.pair.quote.address),
        Network.fromDto(it.pair.quote.network),
        () =>
          CurrencyModel.fromTokenMeta(it.pair.quote, options.tokenLogosHost),
      ),
      base: currencyRegistry.getOrCreate(
        CurrencyAddress(it.pair.base.address),
        Network.fromDto(it.pair.base.network),
        () => CurrencyModel.fromTokenMeta(it.pair.base, options.tokenLogosHost),
      ),
    },
    comments: [fromDto(it.comment, { avatarHost: options.avatarHost })],
    totalCount: it.totalCount,
  }))

export const commentFromRespDto = (
  pairAddress: PairAddress,
  user: User.User,
  req: CommentApi.CreateCommentReqDto,
  res: CommentApi.CreateCommentRespDto,
): Comment => {
  const newComment: Comment = {
    id: CommentId(res.commentId),
    pairAddress: PairAddress(pairAddress),
    ...{
      authorDelugeId: user.delugeId,
      author: {
        ...user,
        avatarSrc: user.avatarSrc ?? undefined,
      },
      depth: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      mentions: [],
      replies: [],
      totalReplies: 0,
    },
    ...req,
    parentCommentId: req.parentCommentId
      ? CommentId(req.parentCommentId)
      : undefined,
  }

  return newComment
}

/**
 * Traverse the comments tree and insert a new reply at the top of the parent comment's replies.
 * If the reply is not associated with a parent comment, it is added to the end of the thread.
 * @param comments - The array of comments representing the thread.
 * @param reply - The new reply comment to be inserted.
 * @returns The updated array of comments with the new reply inserted.
 */
export const threadInsertReplies = (
  comments: Array<Comment>,
  reply: Comment,
): Array<Comment> => {
  // If the comment is not a reply, add it to the end of the thread
  if (!reply.parentCommentId) {
    return [...comments, reply]
  }

  const parent = comments.find((c) => c.id === reply.parentCommentId)

  if (parent) {
    parent.replies = [reply, ...parent.replies]
    parent.totalReplies = parent.replies.length + 1
  } else {
    comments.map((comment) => threadInsertReplies(comment.replies, reply))
  }

  return comments
}

/**
 * Recursively updates a comment within a thread based on the given updated comment.
 *
 * @param {Comment[]} thread - The array of comments representing the thread.
 * @param {Comment} commentUpdated - The updated comment object containing new data.
 * @returns {Comment[]} A new array of comments with the specified comment updated.
 */
export const threadUpdateComment = (
  thread: Array<Comment>,
  commentUpdated: Comment,
): Array<Comment> => {
  return thread.map((comment) => {
    if (comment.id === commentUpdated.id) {
      return { ...comment, ...commentUpdated }
    }

    return {
      ...comment,
      replies: threadUpdateComment(comment.replies, commentUpdated),
    }
  })
}

/**
 * Recursively deletes a comment from a thread based on the given comment ID.
 *
 * @param {Comment[]} thread - The array of comments representing the thread.
 * @param {CommentId} commentId - The ID of the comment to be deleted.
 * @returns {Comment[]} A new array of comments with the specified comment removed.
 */
export const threadDeleteComment = (
  thread: Array<Comment>,
  commentId: CommentId,
): Array<Comment> =>
  thread
    .filter((comment) => comment.id !== commentId)
    .map((comment) => ({
      ...comment,
      replies: threadDeleteComment(comment.replies, commentId),
      totalReplies: comment.replies.length - 1,
    }))

/**
 * Recursively finds a comment within a thread based on the given comment ID.
 *
 * @param {Comment[]} thread - The array of comments representing the thread.
 * @param {CommentId} commentId - The ID of the comment to be found.
 * @returns {Comment | undefined} The found comment or undefined if not found.
 */
export const threadFindComment = (
  thread: Array<Comment>,
  commentId: CommentId,
): Comment | undefined => {
  const found = thread.find((c) => c.id === commentId)

  if (found) {
    return found
  }

  return thread
    .map((comment) => threadFindComment(comment.replies, commentId))
    .find((c) => c !== undefined)
}
